import { useState } from "react";
import { CircleLoader } from "../../components/CircleLoader";
import { EmbeddedParentForm } from "./embedded_parents";
import { AiOutlinePlus } from "react-icons/ai";
import { uploadAccept } from "../../utilities/importantFns";

const PRIMARY = "#0F2A6B";

export const EditStudentForm = ({
  form,
  handleChange,
  handleSubmit,
  classrooms,
  isLoading,
  onParentUnlinked,
}: any) => {
  const [preview, setPreview] = useState<any>(null);

  console.log(form);
  const getImageSrc = () => {
    if (preview?.name) {
      return URL.createObjectURL(preview);
    }

    if (typeof form.profile_picture === "string") {
      return form.profile_picture;
    }

    if (
      form.profile_picture &&
      typeof form.profile_picture === "object" &&
      form.profile_picture?.props?.src
    ) {
      return form.profile_picture.props.src;
    }

    return null;
  };
  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full max-h-[85vh]">
      {/* HEADER */}
      <div className="px-6 py-4 border-b bg-white">
        <h2 className="text-lg font-semibold text-gray-800">Edit Student</h2>
      </div>

      {/* BODY */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-blue-50">
        {/* PROFILE SECTION */}
        <div className="flex items-center gap-6">
          <label className="cursor-pointer flex flex-col items-center gap-3">
            <input
              type="file"
              name="profile_picture"
              accept={uploadAccept.image}
              className="hidden"
              onChange={(e: any) => {
                handleChange(e);
                setPreview(e.target.files[0]);
              }}
            />

            <div
              className="
                w-40 h-40 rounded-full overflow-hidden
                border-4 border-white
                shadow-[0_10px_30px_rgba(0,0,0,0.15)]
                hover:shadow-[0_20px_50px_rgba(15,42,107,0.25)]
                transition-all duration-300
                bg-gray-100 relative group
              "
            >
              {getImageSrc() ? (
                <img
                  src={getImageSrc()}
                  alt="profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <AiOutlinePlus className="text-gray-400 text-3xl" />
                </div>
              )}

              {/* HOVER OVERLAY */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                <span className="text-white text-xs">Change</span>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">
                Profile Picture
              </p>
              <p className="text-xs text-gray-400">Click to update</p>
            </div>
          </label>
        </div>

        {/* BASIC INFO */}
        <Section title="Basic Information">
          <div className="grid grid-cols-2 gap-4">
            <Input
              name="firstname"
              label="First Name"
              value={form.firstname}
              onChange={handleChange}
            />
            <Input
              name="surname"
              label="Surname"
              value={form.surname}
              onChange={handleChange}
            />
            <Input
              name="othername"
              label="Other Name"
              value={form.othername}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <Gender form={form} onChange={handleChange} />
            <Input
              type="date"
              name="date_of_birth"
              label="Date of Birth"
              value={form.date_of_birth}
              onChange={handleChange}
            />
          </div>
        </Section>

        {/* ENROLLMENT */}
        <Section title="Enrollment">
          <div className="grid grid-cols-2 gap-4">
            <select
              name="classroom_id"
              value={form.classroom_id || ""}
              onChange={handleChange}
              className="input"
            >
              <option value="">Select Class</option>
              {classrooms.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <Input
              type="date"
              name="admission_date"
              label="Admission Date"
              value={form.admission_date}
              onChange={handleChange}
            />
          </div>
        </Section>

        {/* PARENTS */}
        <Section title="Parents / Guardians">
          <EmbeddedParentForm
            numberOfParents={form?.parents?.length}
            form={form.parents}
            handleParentChange={(idx: number, e: any, updatedParents?: any[]) => {
              if (handleChange && updatedParents) {
                handleChange({
                  target: { name: "parents", value: updatedParents },
                });
              }
            }}
            studentEdittingSaved={true}
            studentId={form?.id}
            onParentUnlinked={onParentUnlinked}
          />
        </Section>
      </div>

      {/* FOOTER */}
      <div className="px-6 py-4 border-t bg-white flex justify-end">
        <button
          className="px-6 py-2.5 rounded-lg text-white font-medium transition-all hover:shadow-lg"
          style={{ backgroundColor: PRIMARY }}
        >
          {isLoading ? (
            <div className="flex gap-2 items-center">
              <CircleLoader /> Saving...
            </div>
          ) : (
            "Save Changes"
          )}
        </button>
      </div>
    </form>
  );
};

/* ================= UI ================= */

const Section = ({ title, children }: any) => (
  <div className="bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition">
    <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
    {children}
  </div>
);

const Input = ({ label, ...props }: any) => (
  <div>
    <label className="text-xs text-gray-500">{label}</label>
    <input
      {...props}
      className="w-full mt-1 px-3 py-2.5 rounded-lg border bg-gray-50
      hover:border-blue-400 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition"
    />
  </div>
);

const Gender = ({ form, onChange }: any) => (
  <div>
    <p className="text-xs text-gray-500 mb-1">Gender</p>
    <div className="flex gap-2">
      {["male", "female"].map((g) => (
        <div
          key={g}
          onClick={() => onChange({ target: { name: "gender", value: g } })}
          className={`flex-1 text-center py-2 rounded-lg cursor-pointer border transition-all
          ${form.gender === g ? "text-white" : "bg-white"}`}
          style={{
            backgroundColor: form.gender === g ? PRIMARY : "",
          }}
        >
          {g === "male" ? "Boy" : "Girl"}
        </div>
      ))}
    </div>
  </div>
);
